// «Fattura» (#4, richiesta Davide 21/07): selezione righe + creazione dei
// documenti in Eureka via MCP.
//
// 🔴 Questa è l'azione più pericolosa della pagina: scrive documenti fiscali
// VERI in un gestionale di produzione, e l'MCP non ha una delete. Per questo il
// flusso è a DUE passi obbligati — anteprima, poi conferma — che è anche il
// principio dichiarato del progetto («propose + approve su tutto», spec §2).
//
// L'anteprima non è una schermata di cortesia: è il momento in cui il backend
// costruisce i payload veri, controlla anti-doppione, quadratura righe/importo
// e recapito SdI, e dice riga per riga cosa creerà e cosa si rifiuta di creare.
// Nessuna riga bloccata viene creata «lo stesso».
//
// Dopo la conferma NON si aspetta più la risposta di una POST lunga minuti: il
// batch parte in background e questa finestra lo GUARDA (polling). Chiuderla
// non lo ferma — la pagina lo ritrova con la banda «Emissione in corso».
import { useCallback, useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { formatMoney } from "@/shared/format";
import { anteprimaDocumenti, avviaBatch, fetchBatch } from "../api";
import { formatDateIt } from "./columns";
import type {
  BatchRow,
  BatchStatus,
  CreateDocumentsResult,
  CreateDocumentsRow,
  Proposal,
} from "../types";

// SF e ST sono corrispettivi: nascono già completi, non passano dallo SdI.
// Tutto il resto va ancora trasmesso a mano con F11 dentro Eureka: dirlo qui
// evita che «creato» venga letto come «inviato all'Agenzia delle Entrate».
const SENZA_SDI = new Set(["SF", "ST"]);

// Ogni quanto si richiede lo stato del batch. Un documento sono 2-4 chiamate
// MCP, cioè qualche secondo: sotto il secondo si riceverebbe N volte la stessa
// risposta, sopra i due il contatore sembrerebbe fermo.
const POLL_MS = 1200;

// Quante richieste di stato consecutive possono fallire prima di dire che si è
// perso il contatto. Sono ~6 secondi di buco: abbastanza per un blip di rete,
// poco per lasciare qualcuno a fissare una barra che non si muove.
const MAX_POLL_FALLITI = 5;

/** Stati che vanno letti in rosso: la riga non ha prodotto un documento e il
 *  perché sta in `messaggio`. `non_elaborata` sta fuori apposta — non è un
 *  fallimento della riga, è una riga mai tentata. */
const STATI_KO = new Set(["bloccato", "errore", "interrotto"]);

/** Righe che nel diario non hanno ancora né payload né risposta: finché sono
 *  qui, quello che abbiamo letto NON è il loro diario, è la loro assenza. */
const STATI_SENZA_DIARIO = new Set(["in_attesa", "in_corso"]);

function Riga({
  r,
  onCorreggi,
}: {
  r: CreateDocumentsRow;
  /** Apre la «Modifica» su QUESTA riga. Senza, un blocco costava: chiudi
   *  l'anteprima, ritrova la riga (le righe senza data stanno in fondo alla
   *  lista, tagliata a 100), correggi, ri-spunta tutto il batch, riapri. */
  onCorreggi?: (proposalId: number) => void;
}) {
  const bloccata = !r.ok;
  return (
    <tr className={`border-t border-line/50 ${bloccata ? "bg-rose-500/5" : ""}`}>
      <td className="px-2 py-1.5 align-top">
        <span className="block truncate font-medium text-ink" title={r.order_ref ?? undefined}>
          {r.order_ref ?? "manuale"}
        </span>
        <span className="block truncate text-[11px] text-ink-2">{r.customer_name}</span>
      </td>
      <td className="px-2 py-1.5 align-top whitespace-nowrap text-ink-2">{r.doc_type ?? "—"}</td>
      {/* La data che il documento AVRÀ, non quella di fatturazione: quando il
          documento è in ritardo la regola #3 lo fa slittare alla data
          dell'ultimo della serie, e chi emette deve saperlo PRIMA. Se le due
          divergono si mostrano entrambe, altrimenti la seconda riga è rumore. */}
      <td className="px-2 py-1.5 align-top whitespace-nowrap text-ink-2">
        {r.data_documento ? (
          <>
            <span className="block">{formatDateIt(r.data_documento)}</span>
            {r.invoice_date && !r.invoice_date.startsWith(r.data_documento.slice(0, 10)) ? (
              <span
                className="block text-[11px] text-ink-2"
                title="Il documento slitta alla data dell'ultimo della sua serie: numero e data devono crescere insieme."
              >
                fatt. {formatDateIt(r.invoice_date)}
              </span>
            ) : null}
          </>
        ) : (
          formatDateIt(r.invoice_date)
        )}
      </td>
      <td className="px-2 py-1.5 align-top whitespace-nowrap text-right tabular-nums text-ink-2">
        {r.gross_expected != null ? formatMoney(Number(r.gross_expected)) : "—"}
      </td>
      {/* Solo «pronta» o il motivo del blocco: numero ed errore di emissione
          arrivano dal batch, non da qui — questa riga non crea niente. */}
      <td className="px-2 py-1.5 align-top">
        {bloccata ? (
          <>
            <span className="text-[11.5px] text-rose-700">{r.blocco ?? "non creabile"}</span>
            {onCorreggi && (
              <button
                type="button"
                onClick={() => onCorreggi(r.proposal_id)}
                className="ml-1.5 rounded-md border border-line bg-paper-2 px-1.5 py-0.5 text-[11px] text-ink-2 transition-colors duration-[120ms] hover:brightness-95"
              >
                Correggi
              </button>
            )}
          </>
        ) : (
          <span className="text-[11.5px] text-emerald-700">pronta</span>
        )}
      </td>
    </tr>
  );
}

/** Esito della riga durante il batch. Il testo dice sempre a che punto siamo,
 *  perché è l'unica cosa che si guarda mentre l'emissione gira. */
function EsitoBatch({ r }: { r: BatchRow }) {
  if (r.stato === "creato") {
    return (
      <span className="text-[11.5px] font-medium text-emerald-700">
        {r.doc_type} n.{r.numero}
        {r.data_documento ? ` del ${formatDateIt(r.data_documento)}` : ""}
      </span>
    );
  }
  if (r.stato === "in_corso") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-ink">
        {/* Indicatore di attività, non spinner: la riga è già la sua forma. */}
        <span className="size-1.5 animate-pulse rounded-full bg-accent" aria-hidden="true" />
        creo in Eureka…
      </span>
    );
  }
  if (r.stato === "in_attesa") return <span className="text-[11.5px] text-ink-2">in attesa</span>;
  if (r.stato === "non_elaborata") {
    return (
      <span className="text-[11.5px] text-ink-2">non elaborata — il batch si è fermato prima</span>
    );
  }
  return (
    <span className="text-[11.5px] font-medium text-rose-700">
      {r.messaggio ?? (r.stato === "interrotto" ? "interrotta da un riavvio" : "non creata")}
    </span>
  );
}

function RigaBatch({
  r,
  dettaglio,
  onApriDettagli,
}: {
  r: BatchRow;
  /** La stessa riga letta con `?completo=true`, se i dettagli sono arrivati. */
  dettaglio: BatchRow | undefined;
  onApriDettagli: () => void;
}) {
  const ko = STATI_KO.has(r.stato);
  // Il diario di questa riga vale solo se lo snapshot l'ha vista già chiusa:
  // se l'aveva pescata ancora in coda, payload e risposta sono NULL perché
  // all'epoca non era partito niente — non perché a Eureka non sia stato
  // mandato niente. Finché è così si mostra lo skeleton e si rilegge.
  const diario = dettaglio && !STATI_SENZA_DIARIO.has(dettaglio.stato) ? dettaglio : undefined;
  const sfondo = ko ? "bg-rose-500/5" : r.stato === "in_corso" ? "bg-accent-soft" : "";
  return (
    <tr className={`border-t border-line/50 ${sfondo}`}>
      <td className="px-2 py-1.5 align-top text-[11px] tabular-nums text-ink-2">{r.posizione}</td>
      <td className="px-2 py-1.5 align-top">
        <span className="block truncate font-medium text-ink" title={r.order_ref ?? undefined}>
          {r.order_ref ?? "manuale"}
        </span>
        <span className="block truncate text-[11px] text-ink-2">{r.customer_name}</span>
      </td>
      <td className="px-2 py-1.5 align-top whitespace-nowrap text-ink-2">{r.doc_type ?? "—"}</td>
      <td className="px-2 py-1.5 align-top whitespace-nowrap text-right tabular-nums text-ink-2">
        {r.importo != null ? formatMoney(Number(r.importo)) : "—"}
      </td>
      <td className="px-2 py-1.5 align-top">
        <EsitoBatch r={r} />
        {ko && (
          // Pigro apposta: payload e risposta grezza sono grossi e servono a
          // una riga su venti — si chiedono solo quando qualcuno apre davvero.
          <details
            className="mt-1"
            onToggle={(e) => {
              if (e.currentTarget.open) onApriDettagli();
            }}
          >
            <summary className="cursor-pointer text-[11px] text-ink-2 hover:text-ink">
              Dettagli tecnici
            </summary>
            {diario ? (
              <div className="mt-1 space-y-1">
                <p className="text-[10.5px] font-medium text-ink-2">Payload inviato</p>
                <pre className="max-h-40 overflow-auto rounded bg-paper-2 p-2 tabular-nums text-[10.5px] leading-tight text-ink-2">
                  {JSON.stringify(diario.payload ?? null, null, 2)}
                </pre>
                <p className="text-[10.5px] font-medium text-ink-2">Risposta di Eureka</p>
                <pre className="max-h-40 overflow-auto rounded bg-paper-2 p-2 tabular-nums text-[10.5px] leading-tight text-ink-2">
                  {JSON.stringify(diario.risposta ?? null, null, 2)}
                </pre>
              </div>
            ) : (
              <div className="mt-1 h-16 animate-pulse rounded bg-paper-2" aria-hidden="true" />
            )}
          </details>
        )}
      </td>
    </tr>
  );
}

export function CreateDocumentsDialog({
  proposals,
  batchIniziale,
  onClose,
  onDone,
  onCorreggi,
}: {
  proposals: Proposal[];
  /** Batch già avviato da riaprire: si salta l'anteprima e si guarda e basta
   *  (è la banda «Emissione in corso» della pagina che passa di qui). */
  batchIniziale?: string;
  onClose: () => void;
  onDone: () => void;
  /** Chiude l'anteprima e apre la «Modifica» sulla riga bloccata. */
  onCorreggi?: (proposalId: number) => void;
}) {
  const [anteprima, setAnteprima] = useState<CreateDocumentsResult | null>(null);
  const [batchId, setBatchId] = useState<string | null>(batchIniziale ?? null);
  const [batch, setBatch] = useState<BatchStatus | null>(null);
  const [dettagli, setDettagli] = useState<Record<number, BatchRow> | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const ids = proposals.map((p) => p.id);
  const correggibili = new Set(proposals.filter((p) => !p.document_number).map((p) => p.id));

  // L'anteprima parte da sola all'apertura: è la ragione per cui il dialog
  // esiste, farla chiedere sarebbe un click a vuoto. Non quando si sta
  // riaprendo un batch già avviato: lì l'anteprima è acqua passata.
  useEffect(() => {
    if (batchIniziale) return;
    let vivo = true;
    void (async () => {
      try {
        const r = await anteprimaDocumenti(ids);
        if (vivo) setAnteprima(r);
      } catch (err) {
        if (vivo) setError(prettifyApiError(err));
      } finally {
        if (vivo) setBusy(false);
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ids è stabile per apertura
  }, []);

  // Polling dello stato: il batch gira nel backend, qui si guarda soltanto.
  // ponytail: `setTimeout` a catena e non `setInterval` — se una risposta
  // tarda, il giro dopo parte da quando è arrivata, non si accodano richieste.
  useEffect(() => {
    if (!batchId) return;
    let vivo = true;
    let timer = 0;
    let falliti = 0;
    const giro = async () => {
      try {
        const stato = await fetchBatch(batchId);
        if (!vivo) return;
        falliti = 0;
        setError(null);
        setBatch(stato);
        // La risposta che dice `in_corso:false` è GIÀ quella definitiva
        // (contiene l'esito dell'ultima riga): un fetch in più direbbe la
        // stessa cosa.
        if (stato.in_corso) timer = window.setTimeout(() => void giro(), POLL_MS);
        else setBusy(false);
      } catch (err) {
        if (!vivo) return;
        // Un batch dura minuti: una singola richiesta persa (blip di rete, 502
        // del proxy) NON è il batch fallito — quello continua a girare nel
        // backend. Fermare qui il polling mostrerebbe un errore per un'
        // emissione che sta andando benissimo. Si insiste, e solo dopo qualche
        // tentativo si dice che si è perso il contatto — chiarendo che il
        // batch prosegue lo stesso.
        falliti += 1;
        if (falliti < MAX_POLL_FALLITI) {
          timer = window.setTimeout(() => void giro(), POLL_MS);
          return;
        }
        setError(
          `${prettifyApiError(err)} — ho perso il contatto con l'avanzamento, ma l'emissione ` +
            "continua nel backend: riapri la pagina fra poco per vedere dov'è arrivata.",
        );
        setBusy(false);
      }
    };
    setBusy(true);
    void giro();
    return () => {
      vivo = false;
      window.clearTimeout(timer);
    };
  }, [batchId]);

  // 🔴 L'attimo fra «Crea» e l'arrivo del `batch_id`: la POST è in volo, il
  // batch sta GIÀ nascendo nel backend, ma qui non abbiamo ancora l'id con cui
  // seguirlo. Se il componente si smonta adesso, `setBatchId` cade nel vuoto e
  // la pagina chiede la banda a un diario che può non essere ancora committato:
  // risultato, un'emissione viva che scrive documenti fiscali veri e che non si
  // vede più da nessuna parte. Chiudere DOPO invece è legittimo e voluto.
  const avvioInVolo = busy && !batchId && anteprima !== null;

  /** ESC e click fuori fanno esattamente quello che fa il tasto in basso: a
   *  batch avviato «chiudere» vuol dire aver finito (la pagina ricarica la
   *  worklist e azzera la selezione), prima è un annullamento. Senza questo,
   *  uscire con ESC da un'emissione conclusa lasciava a schermo le stesse righe
   *  «da fatturare», senza numero e ancora spuntate — cioè una schermata che
   *  afferma il contrario di quanto è appena successo in Eureka. */
  const chiudi = useCallback(() => {
    if (avvioInVolo) return;
    if (batchId) onDone();
    else onClose();
  }, [avvioInVolo, batchId, onClose, onDone]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") chiudi();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [chiudi]);

  async function conferma() {
    setBusy(true);
    setError(null);
    try {
      // Si mandano SOLO gli id che l'anteprima ha dichiarato creabili: se nel
      // frattempo qualcosa è cambiato lato server, il backend ricontrolla
      // comunque tutte le guardie — questo è il primo dei due filtri, non
      // l'unico.
      const creabili = (anteprima?.righe ?? []).filter((r) => r.ok).map((r) => r.proposal_id);
      const { batch_id } = await avviaBatch(creabili);
      // 200 senza id (proxy che risponde a vuoto, risposta troncata): senza
      // questa uscita `avvioInVolo` resterebbe acceso per sempre — ESC, sfondo
      // e unico tasto sono spenti tutti e tre in quell'istante — e la finestra
      // non si chiuderebbe più. L'emissione però è partita davvero: si dice
      // dove ritrovarla, non che non è successo niente.
      if (!batch_id) {
        setError(
          "L'emissione è partita ma il server non ha detto con quale identificativo. " +
            "Ricarica la pagina: la banda «Emissione in corso» la ritrova. Non rilanciarla.",
        );
        setBusy(false);
        return;
      }
      setBatchId(batch_id); // da qui prende il polling
    } catch (err) {
      // 409 compreso: il backend spiega già in italiano che c'è un'emissione
      // aperta, e la banda in cima alla pagina permette di raggiungerla.
      setError(prettifyApiError(err));
      setBusy(false);
    }
  }

  /** Carica payload e risposta grezza di TUTTE le righe in un colpo solo: la
   *  seconda riga aperta non fa una seconda chiamata.
   *
   *  Si rilegge però se la riga che si sta aprendo, nello snapshot che abbiamo,
   *  era ancora in coda. La prima apertura fotografa anche le righe non ancora
   *  tentate — payload e risposta a NULL — e tenendo quella foto una riga che
   *  fallisce DOPO mostrava per sempre due blocchi «null», che si leggono come
   *  «a Eureka non è stato mandato niente». È falso, ed è falso proprio nel
   *  momento in cui il diario serve a capire se il documento esiste già. */
  async function caricaDettagli(proposalId: number) {
    if (!batchId) return;
    const gia = dettagli?.[proposalId];
    if (gia && !STATI_SENZA_DIARIO.has(gia.stato)) return;
    try {
      const completo = await fetchBatch(batchId, true);
      setDettagli(Object.fromEntries(completo.righe.map((r) => [r.proposal_id, r])));
    } catch (err) {
      setError(prettifyApiError(err));
    }
  }

  const righe = anteprima?.righe ?? [];
  const creabili = righe.filter((r) => r.ok).length;
  const bloccate = righe.length - creabili;
  const fatte = batch ? batch.creati + batch.falliti : 0;
  // Il criterio è il NUMERO, non lo stato «creato»: una riga può avere il
  // documento davvero creato in Eureka e finire lo stesso in `errore`, perché
  // la rilettura del totale fallisce o non quadra DOPO che `documento_crea` è
  // andato a buon fine. Il backend infatti conta i creati così
  // (`sum(1 for r in righe if r.numero)`): filtrando sullo stato, la testata
  // diceva «3 create» e questo riquadro «2 non ancora inviate allo SdI» — la
  // terza esiste, va trasmessa con F11, e nessuno glielo diceva più.
  const daInviare = (batch?.righe ?? []).filter(
    (r) => r.numero && r.doc_type && !SENZA_SDI.has(r.doc_type),
  ).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Crea documenti in Eureka"
      data-no-open
      onClick={(e) => {
        if (e.target === e.currentTarget) chiudi();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg border border-line bg-card shadow-popover">
        <div className="border-b border-line px-5 py-4">
          <h3 className="font-display text-[16px] font-bold text-ink">
            {!batchId
              ? "Crea documenti in Eureka"
              : batch?.in_corso === false
                ? "Emissione conclusa"
                : "Emissione in corso"}
          </h3>
          <p className="mt-1 text-[11.5px] leading-snug text-ink-2">
            {batchId
              ? batch
                ? `${fatte}/${batch.totale} elaborate — ${batch.creati} create, ${batch.falliti} non create.`
                : "Leggo lo stato dell'emissione…"
              : busy && !anteprima
                ? "Preparo l'anteprima: controllo anti-doppione, quadratura importi e recapito SdI…"
                : `${creabili} pronte, ${bloccate} bloccate su ${righe.length} selezionate. Niente è ancora stato scritto in Eureka.`}
          </p>
          {batchId && batch && (
            <div
              className="mt-2 h-1.5 overflow-hidden rounded-full bg-paper-2"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={batch.totale}
              aria-valuenow={fatte}
              aria-label="Documenti elaborati"
            >
              <div
                className="h-full rounded-full bg-accent transition-all duration-300"
                style={{ width: `${batch.totale ? (fatte / batch.totale) * 100 : 0}%` }}
              />
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
          {error && (
            <p role="alert" className="mb-3 text-[12px] text-red">
              {error}
            </p>
          )}
          {busy && !anteprima && !batch ? (
            // Skeleton, non spinner: la tabella arriva dove c'è già la sua forma.
            <div className="space-y-1.5" aria-hidden="true">
              {Array.from({ length: Math.max(1, Math.min(6, ids.length)) }).map((_, i) => (
                <div key={i} className="h-9 animate-pulse rounded bg-paper-2" />
              ))}
            </div>
          ) : batchId ? (
            <table className="w-full text-[12px]">
              <thead className="text-left text-[11px] text-ink-2">
                <tr>
                  <th className="px-2 py-1 font-medium">#</th>
                  <th className="px-2 py-1 font-medium">Ordine / cliente</th>
                  <th className="px-2 py-1 font-medium">Sigla</th>
                  <th className="px-2 py-1 text-right font-medium">Importo</th>
                  <th className="px-2 py-1 font-medium">Esito</th>
                </tr>
              </thead>
              <tbody>
                {(batch?.righe ?? []).map((r) => (
                  <RigaBatch
                    key={r.proposal_id}
                    r={r}
                    dettaglio={dettagli?.[r.proposal_id]}
                    onApriDettagli={() => void caricaDettagli(r.proposal_id)}
                  />
                ))}
              </tbody>
            </table>
          ) : righe.length === 0 ? (
            <p className="py-6 text-center text-[12.5px] text-ink-2">Nessuna riga da creare.</p>
          ) : (
            <table className="w-full text-[12px]">
              <thead className="text-left text-[11px] text-ink-2">
                <tr>
                  <th className="px-2 py-1 font-medium">Ordine / cliente</th>
                  <th className="px-2 py-1 font-medium">Sigla</th>
                  <th className="px-2 py-1 font-medium">Data doc.</th>
                  <th className="px-2 py-1 text-right font-medium">Importo</th>
                  <th className="px-2 py-1 font-medium">Stato</th>
                </tr>
              </thead>
              <tbody>
                {righe.map((r) => (
                  <Riga
                    key={r.proposal_id}
                    r={r}
                    // Niente «Correggi» su una riga bloccata perché il
                    // documento c'è già: la PATCH la rifiuterebbe comunque
                    // (`_ensure_not_emitted`), ma solo DOPO aver riaperto e
                    // compilato la Modifica. In tabella quel tasto è nascosto
                    // per la stessa ragione.
                    onCorreggi={correggibili.has(r.proposal_id) ? onCorreggi : undefined}
                  />
                ))}
              </tbody>
            </table>
          )}

          {batchId && daInviare > 0 && (
            <div className="mt-3 rounded-md border border-orange-500/40 bg-orange-500/10 px-3 py-2 text-[11.5px] leading-snug text-orange-800">
              <strong>{daInviare} documenti non sono ancora stati inviati allo SdI.</strong> Fatture
              e note di credito nascono create ma non trasmesse: la trasmissione è il tasto{" "}
              <code className="rounded bg-white/60 px-1">F11 Stampa</code> dentro Eureka, che l'MCP
              non può fare. Le trovi nel tab «Da inviare».
            </div>
          )}
          {batchId && batch?.in_corso !== false && (
            <p className="mt-3 text-[11px] leading-snug text-ink-2">
              L'emissione gira sul server: <strong>puoi chiudere questa finestra</strong>, il batch
              va avanti lo stesso. La pagina mostra in cima «Emissione in corso» finché non finisce.
            </p>
          )}
          {!batchId && creabili > 0 && (
            <p className="mt-3 text-[11px] leading-snug text-ink-2">
              I documenti verranno creati <strong>uno alla volta, in ordine di data</strong>: è
              quello che tiene numero e data coerenti nella serie. SF e ST nascono complete; FA,
              OSS, RC e RCO restano da trasmettere con F11 in Eureka.
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">
          <button
            type="button"
            // Chiudere non ferma niente: durante il batch il tasto resta vivo
            // apposta, disabilitarlo darebbe l'idea contraria. L'unico momento
            // in cui è spento è mentre l'avvio è in volo (vedi `avvioInVolo`).
            disabled={avvioInVolo}
            title={avvioInVolo ? "Sto avviando l'emissione: un istante e la vedi qui" : undefined}
            onClick={chiudi}
            className="rounded-md border border-line bg-card px-3 py-2 text-[12px] font-medium text-ink-2 hover:border-ink-4 disabled:opacity-50"
          >
            {!batchId ? "Annulla" : batch?.in_corso === false ? "Chiudi" : "Chiudi (continua)"}
          </button>
          {!batchId && (
            <button
              type="button"
              disabled={busy || creabili === 0}
              onClick={() => void conferma()}
              title={
                creabili === 0
                  ? "Nessuna riga è creabile: risolvi prima i motivi indicati"
                  : `Crea ${creabili} documenti in Eureka`
              }
              className="rounded-md border border-accent bg-accent-2 px-3 py-2 text-[12px] font-medium text-white disabled:opacity-50"
            >
              {busy ? "Avvio…" : `Crea ${creabili} document${creabili === 1 ? "o" : "i"}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
